import React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/utils/cn';

import { Building2Icon, CheckIcon, ChevronDownIcon, SearchIcon } from './Icons';

interface ClientBase {
    id: string;
    name: string;
    icon?: string;
}

interface ClientBaseDropdownProps {
    clientBases: ClientBase[];
    selected: string[];
    onChange: (selected: string[]) => void;
    placeholder?: string;
}

export const ClientBaseDropdown = ({
    clientBases,
    selected,
    onChange,
    placeholder = 'Select client bases…',
}: ClientBaseDropdownProps) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const filtered = useMemo(
        () => clientBases.filter((cb) => cb.name.toLowerCase().includes(search.toLowerCase())),
        [clientBases, search],
    );

    const allSelected = selected.length === clientBases.length;
    const noneSelected = selected.length === 0;

    useEffect(() => {
        if (open) searchRef.current?.focus();
    }, [open]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const toggle = (id: string) => {
        onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
    };

    const toggleAll = () => {
        onChange(allSelected ? [] : clientBases.map((cb) => cb.id));
    };

    const selectedBases = clientBases.filter((cb) => selected.includes(cb.id));
    const displayLimit = 3;

    return (
        <div ref={containerRef} className="dark relative w-full">
            {/* Trigger */}
            <button
                onClick={() => setOpen(!open)}
                className={cn(
                    'border-input bg-background flex w-full cursor-pointer items-center gap-2.5 rounded-md border px-2.5 py-3 text-sm transition-all',
                    'hover:border-primary/40 focus:ring-primary/20 focus:border-primary/60 focus:ring-2 focus:outline-none',
                    open && 'border-primary/60 ring-primary/20 ring-2',
                )}
            >
                <Building2Icon className="text-muted-foreground h-[18px] w-[18px] shrink-0" />

                <div className="flex min-h-[24px] flex-1 flex-wrap items-center gap-1.5">
                    {noneSelected && <span className="text-muted-foreground">{placeholder}</span>}
                    {allSelected && (
                        <span className="bg-primary text-primary-foreground inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium">
                            All client bases
                        </span>
                    )}
                    {!allSelected &&
                        selectedBases.slice(0, displayLimit).map((cb) => (
                            <span
                                key={cb.id}
                                className="bg-secondary text-secondary-foreground hover:bg-secondary/80 border-border/60 inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium transition-colors"
                            >
                                {cb.name}
                            </span>
                        ))}
                    {!allSelected && selectedBases.length > displayLimit && (
                        <span className="text-muted-foreground text-xs font-medium">
                            +{selectedBases.length - displayLimit} more
                        </span>
                    )}
                </div>

                <ChevronDownIcon
                    className={cn(
                        'text-muted-foreground h-[18px] w-[18px] shrink-0 transition-transform duration-200',
                        open && 'rotate-180',
                    )}
                />
            </button>

            {/* Dropdown */}
            {open && (
                <div className="border-border bg-popover text-popover-foreground animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 absolute z-50 mt-1.5 w-full rounded-lg border shadow-lg duration-150">
                    {/* Search */}
                    <div className="border-border flex items-center gap-2.5 border-b px-2.5 py-3">
                        <SearchIcon className="text-muted-foreground h-[18px] w-[18px]" />
                        <input
                            ref={searchRef}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search…"
                            className="placeholder:text-muted-foreground flex-1 bg-transparent text-sm outline-none"
                        />
                    </div>

                    {/* Select All */}
                    {!search && (
                        <button
                            onClick={toggleAll}
                            className="hover:bg-muted/50 border-border flex w-full cursor-pointer items-center gap-2.5 border-b px-2.5 py-3 text-sm transition-colors"
                        >
                            <div
                                className={cn(
                                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                                    allSelected ? 'border-primary bg-primary' : 'border-input',
                                )}
                            >
                                {allSelected && <CheckIcon className="text-primary-foreground h-3 w-3" />}
                            </div>
                            <span className="text-foreground font-medium">Select all</span>
                            <span className="text-muted-foreground ml-auto text-xs">{clientBases.length} bases</span>
                        </button>
                    )}

                    {/* List */}
                    <div className="max-h-56 overflow-y-auto py-1">
                        {filtered.length === 0 && (
                            <p className="text-muted-foreground px-3 py-4 text-center text-sm">No client bases found</p>
                        )}
                        {filtered.map((cb) => {
                            const isSelected = selected.includes(cb.id);
                            return (
                                <div
                                    key={cb.id}
                                    className="group hover:bg-muted/50 flex w-full items-center gap-2.5 px-2.5 py-3 text-sm transition-colors"
                                >
                                    <button
                                        onClick={() => toggle(cb.id)}
                                        className="flex flex-1 cursor-pointer items-center gap-2.5"
                                    >
                                        <div
                                            className={cn(
                                                'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                                                isSelected ? 'border-primary bg-primary' : 'border-input',
                                            )}
                                        >
                                            {isSelected && <CheckIcon className="text-primary-foreground h-3 w-3" />}
                                        </div>
                                        <span className={cn('text-foreground', isSelected && 'font-medium')}>
                                            {cb.name}
                                        </span>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onChange([cb.id]);
                                        }}
                                        className="text-primary hover:bg-primary/10 hidden cursor-pointer rounded px-1.5 py-0.5 text-xs font-medium transition-colors group-hover:inline-flex"
                                    >
                                        Only
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
